import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [role, setRole] = useState(() => localStorage.getItem('role'));
  const [umemid, setUserMemberId] = useState(() => localStorage.getItem('umemid'));
  const [username, setUserName] = useState(() => localStorage.getItem('username'));
  const [resultsPerPage, setResutlsPerPage] = useState(() => localStorage.getItem('rowcount'));
  
  const [showWarning, setShowWarning] = useState(false); // Track if the warning modal should be shown
  const [countdown, setCountdown] = useState(0); // Countdown state for the warning
  const history = useHistory();
  
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null); // Ref for the warning timeout
  const countdownRef = useRef(null); // Ref for countdown interval

  // Get session timeout and warning time from env variables
  const sessionTimeoutMinutes = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '15', 10);
  const warningTimeSeconds = parseInt(process.env.REACT_APP_WARNING_TIMEOUT || '30', 10); // Configurable warning time in seconds
  
  const sessionTimeoutMs = sessionTimeoutMinutes * 60 * 1000; // Convert session timeout to milliseconds
  const warningTimeMs = warningTimeSeconds * 1000; // Convert warning time to milliseconds

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('expiryTime');
    localStorage.removeItem('umemid');
    localStorage.removeItem('username');
    localStorage.removeItem('rowcount');
    
    // Clear all timeouts and intervals related to session
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
  
    // Hide the warning modal
    setShowWarning(false);
  
    // Reset states
    setToken(null);
    setRole(null);
    setUserMemberId(null);
    setUserName(null);
    setResutlsPerPage(null);
  
    // Redirect to login page
    history.push('/login');
  }, [history]);

  const updateResultsPerPage = useCallback((newResultsPerPage) => {
    localStorage.setItem('rowcount', newResultsPerPage);
    setResutlsPerPage(newResultsPerPage);
  }, []);

  // Function to reset the session timer
  const resetSessionTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    setShowWarning(false); // Hide the warning alert if it was shown

    // Set a new warning timeout that will trigger the alert before logout
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true); // Show the warning
      setCountdown(warningTimeSeconds); // Start the countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev === 1) {
            clearInterval(countdownRef.current); // Stop countdown at 0
          }
          return prev - 1;
        });
      }, 1000); // Update countdown every second
    }, sessionTimeoutMs - warningTimeMs);
  
    // Set the main timeout to log out the user after the full session time
    timeoutRef.current = setTimeout(logout, sessionTimeoutMs);
  }, [logout, sessionTimeoutMs, warningTimeMs, warningTimeSeconds]);

  // Hook to monitor user activity and reset the session timer
  useEffect(() => {
    const handleUserActivity = () => {
      resetSessionTimeout(); // Reset session timeout on any user activity
    };

    // Reset the timeout on user interactions
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    // Set the initial timeout when the component mounts
    resetSessionTimeout();

    return () => {
      // Clean up event listeners and timeout on component unmount
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [resetSessionTimeout]);

  // Token expiration effect
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedRole = localStorage.getItem('role');
    const storedUmemid = localStorage.getItem('umemid');
    const storedUsername = localStorage.getItem('username');
    const storedRowcount = localStorage.getItem('rowcount');
    const expiryTime = localStorage.getItem('expiryTime');
    const currentTime = new Date().getTime();

    if (expiryTime && currentTime > parseInt(expiryTime)) {
      // Token has expired
      logout();
    } else {
      // Token is valid, restore state
      if (storedToken) {
        setToken(storedToken);
      }
      if (storedRole) {
        setRole(storedRole);
      }
      if (storedUmemid) {
        setUserMemberId(storedUmemid);
      }
      if (storedUsername) {
        setUserName(storedUsername);
      }
      if (storedRowcount) {
        setResutlsPerPage(storedRowcount);
      }
    }
  }, [logout]);

  const login = useCallback(
    (newToken, newRole, newExpiryTime, newUmemid, newUsername,newResultsPerPage) => {
      localStorage.setItem('token', newToken);
      localStorage.setItem('role', newRole);
      localStorage.setItem('expiryTime', newExpiryTime.toString());
      localStorage.setItem('umemid', newUmemid);
      localStorage.setItem('username', newUsername);
      localStorage.setItem('rowcount', newResultsPerPage);
      setToken(newToken);
      setRole(newRole);
      setUserMemberId(newUmemid);
      setUserName(newUsername);
      setResutlsPerPage(newResultsPerPage);
      history.push('/');
    },
    [history]
  );

  const isAdmin = useCallback(() => {
    return role === 'admin';
  }, [role]);

  const handleStayLoggedIn = () => {
    resetSessionTimeout(); // Reset the session timer when user clicks "Stay Logged In"
    setShowWarning(false); // Hide the warning modal
  };

  return (
    <AuthContext.Provider
      value={{ token, role, umemid, username, resultsPerPage,isAdmin, login, logout,updateResultsPerPage }}
    >
      {children}
      {/* Warning Modal */}
      {showWarning && (
        <div className="session-warning-modal">
          <p>Your session will expire in {countdown} seconds. Do you want to stay logged in?</p>
          <button onClick={handleStayLoggedIn}>Stay Logged In</button>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };